import { ICandyApiDraftClient } from "../../../../../Common/CandyApiClient/ICandyApiClient";
import { MultiLineSort, SortEnum } from "../../../Controls/MultiControls/MultiLine/MultiLineTypes";
import { FilterExpressionToQueryCollectionExpressionVisitor } from "../../../Helpers/FilterExpressions/FilterExpressionToQueryCollectionExpressionVisitor";
import { AbsoluteModelPath, createAbsolute, PathTokens } from "../../../../../Common/ModelPath/ModelPath";
import { AndExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/AndExpression";
import { InnerQueryRequest } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/InnerQueryRequest";
import { SelectClause } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/SelectClause";
import { InstanceIdCallExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/InstanceIdCallExpression";
import { isNotNullOrUndefined } from "../../../../../Common/TypingUtils";
import { SortingRule } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/SortingRule";
import { AbsoluteModelFieldPath } from "../../../../../Common/ModelPath/AbsoluteModelFieldPath";
import { FullFormState } from "../Store/FullFormState";
import { currentViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { FilterExpression } from "../../../Helpers/FilterExpressions/FilterExpression";
import {
    DataFieldRow,
    DataFieldValue,
} from "../../../../../Common/CandyApiClient/Types/QueryCollecton/InnerQueryResponse";
import { ValueReferenceExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/ValueReferenceExpression";
import { CountCallExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/CountCallExpression";
import { SumCallExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/SumCallExpression";
import { MaxCallExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/MaxCallExpression";
import { MinCallExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/MinCallExpression";
import { RelativeModelFieldPath } from "../../../../../Common/ModelPath/RelativeModelFieldPath";

import { AggregationType } from "./IAggregationQueryStrategy";
import { FilterResult, IFilteringStrategy } from "./IFilteringStrategy";

const MaxInstancesForServerRequest = 1000000000;

export class ServerSideFilteringStrategy implements IFilteringStrategy {
    private readonly maxInstancesForServerRequest: number = MaxInstancesForServerRequest;
    private readonly getState: () => FullFormState;
    private readonly candyApiDraftClient: ICandyApiDraftClient;

    public constructor(
        getState: () => FullFormState,
        candyApiDraftClient: ICandyApiDraftClient,
        maxInstancesForServerRequest: number = MaxInstancesForServerRequest
    ) {
        this.getState = getState;
        this.candyApiDraftClient = candyApiDraftClient;
        this.maxInstancesForServerRequest = maxInstancesForServerRequest;
    }

    public async performFilteringAndSorting(
        multilinePath: string,
        filters: FilterExpression[],
        multilineSort?: MultiLineSort,
        abortSignal?: AbortSignal
    ): Promise<FilterResult> {
        const filterConverter = new FilterExpressionToQueryCollectionExpressionVisitor(createAbsolute(multilinePath));
        const convertedFilters = filters.map(x => filterConverter.transformExpression(x));
        const combinedFilter =
            convertedFilters.length > 1 ? new AndExpression(convertedFilters) : convertedFilters?.[0];
        const request = new InnerQueryRequest(
            multilinePath,
            [new SelectClause("instance", new InstanceIdCallExpression())],
            combinedFilter,
            multilineSort != undefined
                ? [this.multilineSortToSortingRule(multilineSort, createAbsolute(multilinePath))]
                : undefined,
            undefined,
            0,
            this.maxInstancesForServerRequest
        );
        const response = await this.candyApiDraftClient.queryCollection(request, abortSignal);
        const childrenSet = new Set(currentViewModelSelect.children(this.getState(), multilinePath));
        const instances = Iterator.from(response.data)
            .map(x => x[0])
            .filter(isNotNullOrUndefined)
            .filter(x => childrenSet.has(x))
            .toArray();
        return instances;
    }

    public async performFilteringAndSortingItems(
        multilinePath: string,
        requestPaths: RelativeModelFieldPath[],
        filters: FilterExpression[],
        multilineSort?: MultiLineSort,
        abortSignal?: AbortSignal
    ): Promise<DataFieldRow[]> {
        const filterConverter = new FilterExpressionToQueryCollectionExpressionVisitor(createAbsolute(multilinePath));
        const convertedFilters = filters.map(x => filterConverter.transformExpression(x));
        const combinedFilter =
            convertedFilters.length > 1 ? new AndExpression(convertedFilters) : convertedFilters?.[0];
        const request = new InnerQueryRequest(
            multilinePath,
            [
                new SelectClause("instance", new InstanceIdCallExpression()),
                ...requestPaths.map(requestPath => {
                    const stringRequestPath = requestPath.toString();
                    return new SelectClause(stringRequestPath, new ValueReferenceExpression(stringRequestPath));
                }),
            ],
            combinedFilter,
            multilineSort != undefined
                ? [this.multilineSortToSortingRule(multilineSort, createAbsolute(multilinePath))]
                : undefined,
            undefined,
            0,
            this.maxInstancesForServerRequest
        );
        const response = await this.candyApiDraftClient.queryCollection(request, abortSignal);
        const childrenSet = new Set(currentViewModelSelect.children(this.getState(), multilinePath));
        const items = Iterator.from(response.data)
            .filter(it => isNotNullOrUndefined(it[0]) && childrenSet.has(it[0]))
            .toArray();
        return items;
    }

    public async getAggregationValue(
        multilinePath: string,
        pathToValue: RelativeModelFieldPath,
        aggregationType: AggregationType,
        abortSignal?: AbortSignal
    ): Promise<DataFieldValue> {
        const InnerQueryExpressions = {
            [AggregationType.Count]: CountCallExpression,
            [AggregationType.Sum]: SumCallExpression,
            [AggregationType.Max]: MaxCallExpression,
            [AggregationType.Min]: MinCallExpression,
        };
        const innerQueryExpression = InnerQueryExpressions[aggregationType];

        const request = new InnerQueryRequest(
            multilinePath,
            [
                new SelectClause(
                    String(aggregationType),
                    new innerQueryExpression(new ValueReferenceExpression(pathToValue.toString()))
                ),
            ],
            undefined,
            undefined,
            undefined,
            0,
            this.maxInstancesForServerRequest
        );
        const response = await this.candyApiDraftClient.queryCollection(request, abortSignal);
        return response.data.at(0)?.at(0);
    }

    private multilineSortToSortingRule(sort: MultiLineSort, multiLinePath: AbsoluteModelPath): SortingRule {
        const pathAndField = AbsoluteModelFieldPath.createFromMask(sort.dataPath, PathTokens.each);
        const tailPath = multiLinePath.getSuffixPathFrom(pathAndField.path).trimFirstStarIfFirstToken();
        return new SortingRule(
            `${tailPath.toString()}.${pathAndField.field}`,
            sort.value === SortEnum.Desc ? "Descending" : "Ascending"
        );
    }
}
